import { ICE_COLORS, ICE_LOOK, TONE_COLORS } from '@halvard/board3d';
import type { ice } from '@halvard/rules';
import { Link } from 'react-router';
import { WarriorGlyph } from '../components/WarriorGlyph';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Page.css';

const WARRIORS: {
  type: ice.WarriorType;
  name: string;
  force: number;
  moves: number;
  detail: string;
}[] = [
  {
    type: 'skier',
    name: 'Skier',
    force: 1,
    moves: 4,
    detail: 'Fast enough to reach a runestone first, but the easiest warrior to shove.',
  },
  {
    type: 'spear',
    name: 'Spear',
    force: 2,
    moves: 3,
    detail: 'The ordinary warrior, between the two.',
  },
  {
    type: 'huscarl',
    name: 'Huscarl',
    force: 3,
    moves: 2,
    detail: 'Slow on the ice, but hard to move and hard to stop.',
  },
];

const THICKNESS = ['Thin ice', 'Ice', 'Thick ice', 'Pressure ridge'];

export function Learn() {
  useDocumentTitle('Learn');
  return (
    <article className="page-page">
      <h1>How to play</h1>
      <p>
        Two warbands fight on a frozen lake as the thaw begins. <strong>Brand</strong> (orange)
        comes from ranks 1–3 and attacks. <strong>Rime</strong> (teal) holds ranks 7–9 and defends.
        Every game is played on a new lake of 9×9 squares, and each square has an ice thickness from
        0 to 3, or is a hole of open water.
      </p>
      <ul className="page-swatches" aria-label="Ice thickness">
        {ICE_COLORS.map((color, h) => (
          <li key={color}>
            <span className="page-swatch" style={{ background: color }} aria-hidden="true" />
            {h} · {THICKNESS[h]}
          </li>
        ))}
        <li>
          <span className="page-swatch" style={{ background: ICE_LOOK.water }} aria-hidden="true" />
          Hole
        </li>
      </ul>

      <h2>Winning</h2>
      <ul>
        <li>
          Three <strong>runestones</strong> lie in the middle of the lake: the west, centre and east
          floes of ranks 4–6. A side <strong>holds</strong> a runestone when it has more warriors on
          that floe.
        </li>
        <li>
          After each of its turns, Brand draws <strong>1 Glory for each runestone it holds</strong>.
          <strong> Brand wins with 20 Glory</strong> by the end of round 16.
        </li>
        <li>
          <strong>Rime wins</strong> if Brand doesn’t get there in time.
        </li>
        <li>
          Shoving <strong>every enemy warrior</strong> under the ice wins at once, for either side.
        </li>
      </ul>

      <h2>Setting up</h2>
      <p>
        Once the lake is revealed, the players take turns placing one warrior at a time anywhere in
        their own territory, Brand first. Read the lake before you place: there are no openings to
        learn, only ice. Then Brand moves first.
      </p>

      <h2>Your turn</h2>
      <p>
        Move <strong>one</strong> warrior. It spends its move points, one step at a time, to any of
        the 8 squares around it. A step costs <strong>1 + the difference in thickness</strong>, up
        or down: 1 on level ice, 2 for one level, and so on. Warriors can’t pass through each other
        or enter holes. If none of your warriors can move, you pass.
      </p>

      <h2>Shoving</h2>
      <p>
        There is no capture. The last step of a move may go into an enemy’s square: that is a
        <strong> shove</strong>.
      </p>
      <ul>
        <li>
          <strong>Your strength</strong> is your warrior’s force, +1 for each of your other warriors
          touching the enemy.
        </li>
        <li>
          <strong>Their shield wall</strong> is the enemy’s force, +1 for each of its allies
          touching it.
        </li>
        <li>
          The shove works if your strength is <strong>at least</strong> their shield wall. If it
          would fail, you can’t play it.
        </li>
        <li>
          The enemy slides one square straight away from you, and so does{' '}
          <strong>everyone lined up behind it</strong>, friend or foe. Your warrior takes the square
          it left.
        </li>
        <li>
          A sliding warrior is lost if it ends off the lake or in a hole, or 2 or more levels from
          where it stood: <strong>2 down, it falls</strong> from the ledge and breaks through;{' '}
          <strong>2 up, it is crushed</strong> against the wall of ice. A fall or a crush stops the
          push: nobody beyond it moves.
        </li>
      </ul>

      <h2>The thaw</h2>
      <p>
        At the end of rounds 3, 6, 9 and so on, the ice groans and 2 squares crack. Three rounds
        later each cracked square thaws: it loses a level, and thin ice opens into a hole, taking
        any warrior on it. You see every crack coming, so step off it, or shove an enemy onto it.
      </p>

      <h2>The warriors</h2>
      <p>Each side has 2 of each. Size shows force.</p>
      <ul className="page-pieces">
        {WARRIORS.map((w) => (
          <li key={w.type}>
            <WarriorGlyph side="brand" type={w.type} size={44} />
            <div>
              <h3>
                {w.name} <span className="label">×2</span>
              </h3>
              <p>
                Force {w.force}, {w.moves} move points.
              </p>
              <p>{w.detail}</p>
            </div>
          </li>
        ))}
      </ul>

      <h2>Reading the board</h2>
      <p>
        Select a warrior and dark dots show where it can walk. Every enemy it can reach gets a badge
        with your strength against its shield wall: <Badge tone="works">3:2</Badge> works,{' '}
        <Badge tone="sinks-ally">3:2 !</Badge> also sinks one of yours,{' '}
        <Badge tone="fails">1:3</Badge> fails.
      </p>
      <p>
        Click an enemy to see each way to shove it: a line from your warrior, a diamond on the
        square the shove starts from, and an arrow for the direction it pushes. Faint warriors show
        where the line would land, marked when one would go under. Click a diamond to shove from
        there.
      </p>
      <p>
        A cracked square shows fracture lines and shivers now and then, harder as its thaw nears;
        the lines are red when the thaw will open a hole. A runestone’s ring glows in the colour of
        the side that holds it.
      </p>
      <p>
        <Link to="/play/local" className="btn btn-primary">
          Try it on this screen
        </Link>
      </p>
    </article>
  );
}

function Badge({ tone, children }: { tone: keyof typeof TONE_COLORS; children: string }) {
  return (
    <span
      className="mono"
      style={{
        padding: '1px 7px',
        borderRadius: 999,
        background: TONE_COLORS[tone],
        color: '#0C1822',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}
